import { useEffect, useRef } from 'react';

/**
 * Google One Tap: the small "Continue as …" card Google draws over the sign-in
 * page — in Chrome, the browser's own FedCM account chooser — instead of the
 * popup window `signInWithPopup` opens.
 *
 * It is a shortcut, not the way in: Google stops offering it after a user
 * dismisses it a few times, some browsers block it, and it never shows to
 * someone signed out of Google. So the "Continue with Google" button stays,
 * and this only ever adds to it. With no client ID configured, or Google's
 * script failing to load, it does nothing at all.
 */

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

/** The slice of Google Identity Services this uses. */
interface GoogleIdentity {
  cancel: () => void;
  initialize: (config: {
    auto_select?: boolean;
    callback: (response: { credential?: string }) => void;
    cancel_on_tap_outside?: boolean;
    client_id: string;
    context?: 'signin' | 'signup' | 'use';
    itp_support?: boolean;
    use_fedcm_for_prompt?: boolean;
  }) => void;
  prompt: () => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdentity } };
  }
}

let loading: Promise<GoogleIdentity | null> | null = null;

/** Google's script, once per page; null when it cannot be had. */
export function loadGoogleIdentity(): Promise<GoogleIdentity | null> {
  const ready = window.google?.accounts?.id;
  if (ready) return Promise.resolve(ready);
  loading ??= new Promise((resolve) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = SCRIPT_SRC;
    script.onload = () => {
      resolve(window.google?.accounts?.id ?? null);
    };
    script.onerror = () => {
      // Blocked or offline. Let a later mount try again.
      loading = null;
      resolve(null);
    };
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * Offers One Tap while `enabled`, calling `onCredential` with the Google ID
 * token when someone picks an account.
 *
 * `onCredential` is read through a ref so a re-render with a new handler does
 * not re-prompt; only `enabled` changing does.
 */
export function useGoogleOneTap({
  enabled,
  onCredential,
}: {
  enabled: boolean;
  onCredential: (googleIdToken: string) => void;
}) {
  const handler = useRef(onCredential);
  handler.current = onCredential;

  useEffect(() => {
    if (!enabled || !clientId) return;
    let cancelled = false;
    let identity: GoogleIdentity | null = null;

    void loadGoogleIdentity().then((id) => {
      if (cancelled || !id) return;
      identity = id;
      id.initialize({
        callback: ({ credential }) => {
          if (credential) handler.current(credential);
        },
        // Picking the account is the consent; never sign anybody in unasked.
        auto_select: false,
        cancel_on_tap_outside: false,
        client_id: clientId,
        context: 'signin',
        // Safari and Firefox partition storage, which breaks the classic
        // prompt; this switches them to Google's ITP-safe flow.
        itp_support: true,
        use_fedcm_for_prompt: true,
      });
      id.prompt();
    });

    return () => {
      cancelled = true;
      identity?.cancel();
    };
  }, [enabled]);
}
